import React, { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from 'react';

function createDrafts() {
  const values = new Map<string, string>();
  const listeners = new Set<() => void>();
  return {
    read: (key: string) => values.get(key) ?? '',
    write(key: string, value: string) {
      if (value) values.set(key, value); else values.delete(key);
      listeners.forEach(listener => listener());
    },
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };
}
const DraftContext = createContext<ReturnType<typeof createDrafts> | null>(null);

/** Navigation-only drafts. A new session discards the entire memory store; nothing is persisted/uploaded. */
export function MessageDraftProvider({ session, children }: { session: string; children: React.ReactNode }) {
  // The session is only a lifetime boundary, never a storage key or value.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- changing session intentionally discards all drafts
  const drafts = useMemo(() => createDrafts(), [session]);
  return <DraftContext.Provider value={drafts}>{children}</DraftContext.Provider>;
}

export function useMessageDraft(ownerId: number, peerId: number): [string, (value: string) => void, (expected: string, value: string) => void] {
  const shared = useContext(DraftContext);
  const local = useMemo(() => createDrafts(), []);
  const drafts = shared ?? local;
  const key = `${ownerId}:${peerId}`;
  const read = useCallback(() => drafts.read(key), [drafts, key]);
  const text = useSyncExternalStore(drafts.subscribe, read, read);
  const setText = useCallback((value: string) => drafts.write(key, value), [drafts, key]);
  const replaceText = useCallback((expected: string, value: string) => {
    if (drafts.read(key) === expected) drafts.write(key, value);
  }, [drafts, key]);
  return [text, setText, replaceText];
}
